import {
  ClickEventHandlerInput,
  CreateProductSessionShownEventInput,
  ProductDetails,
  SelectedSessionWithVariant,
  UserBookingSelections,
  Voucher,
  StaticAvailability,
  CreateProductSessionSelectedEventInput,
  TrackBookingFieldEnteredInput,
} from '@/types'
import { Spacing } from '@big-red-group/storefront-common-ui'
import TabSelector from '@/components/TabSelector'
import { BookingWrapper } from '@/components/ProductBooking.styles'
import { logger } from '@/logging/Logger'
import VoucherSelector from '@/components/VoucherSelector'
import BookingNew from '@/components/BookingNew'
import { LastMinutePricingDate } from '@big-red-group/storefront-common-checkout'
import { VoucherSelectorWrap } from './VoucherSelector.styles'
import GiftingCallout from './GiftingCallout'
import { useMemo } from 'react'

export type ProductBookingProps = {
  productDetails: ProductDetails
  retailPrice?: number
  uuid?: string
  activeBookingTab: 1 | 0
  onAddToCart: (isGift: boolean, selectedVariants?: SelectedSessionWithVariant[], voucher?: Voucher) => void
  onUserSelection: (userSelection: UserBookingSelections) => void
  userBookingSelections: UserBookingSelections
  isEdit: boolean
  onSessionChecked?: (
    sessionFrom: Date,
    sessionTo: Date,
    bookingCalendarType?: string,
    hasHotDeal?: boolean,
    hasLastMinutePricing?: boolean
  ) => void
  onSessionShown?: (input: Omit<CreateProductSessionShownEventInput, 'product' | 'url'>) => void
  onBookNowClick?: (params: ClickEventHandlerInput) => void
  lastMinutePricingDates?: LastMinutePricingDate[]
  showGiftingTab: boolean
  staticAvailability: StaticAvailability | {}
  onSessionSelected?: (input: Omit<CreateProductSessionSelectedEventInput, 'product' | 'url'>) => void
  onLinkClick?: (params: ClickEventHandlerInput) => void
  bookingFieldEntered?: (bookingField: TrackBookingFieldEnteredInput) => void
}

const log = logger()

const ProductBooking: React.FC<ProductBookingProps> = ({
  productDetails,
  retailPrice,
  uuid,
  activeBookingTab,
  onAddToCart,
  onUserSelection,
  userBookingSelections,
  lastMinutePricingDates,
  isEdit,
  onSessionChecked,
  onSessionShown,
  onBookNowClick,
  showGiftingTab,
  staticAvailability,
  onLinkClick,
  bookingFieldEntered,
  onSessionSelected,
}) => {
  const evSessions = useMemo(() => {
    return productDetails?.productSessions.filter((session) => session.evSession)
  }, [productDetails?.productSessions])

  const renderBookingComponent = () => {
    return (
      <BookingNew
        displayAfterpay={false}
        productSessionGroups={productDetails.productSessionGroups}
        handlerKey={productDetails.handlerKey}
        productId={productDetails.id}
        productName={productDetails.name}
        operatorId={productDetails.operatorId}
        productImage={productDetails.images[0]}
        fromPrice={productDetails.fromPrice}
        sessions={productDetails.productSessions}
        retailPrice={retailPrice}
        bookingRequired={productDetails.bookingRequired}
        bookingNotesRequired={!!productDetails.bookingNotesRequired}
        bookingNotesPlaceholder={productDetails.bookingNotesPlaceholder ?? ''}
        regionUrlSegment={productDetails.canonicalRegionUrlSegment}
        userBookingSelections={userBookingSelections}
        uuid={uuid}
        operatorName={productDetails.operatorName}
        productUrlSegment={productDetails.urlSegment}
        lastMinutePricingDates={lastMinutePricingDates}
        onAddToCart={onAddToCart}
        onSessionChecked={onSessionChecked}
        onSessionShown={onSessionShown}
        onUserSelection={onUserSelection}
        onBookNowClick={onBookNowClick}
        timezone={productDetails.timezone}
        staticAvailability={staticAvailability}
        bookingFieldEntered={bookingFieldEntered}
        onLinkClick={onLinkClick}
        onSessionSelected={onSessionSelected}
      />
    )
  }
  const bookingTabs = [
    {
      label: productDetails.bookingRequired ? 'Book a date now' : 'Book now',
      children: (
        <>
          <Spacing size="M" />
          {renderBookingComponent()}
        </>
      ),
    },
    {
      label: 'Gift this experience',
      children: (
        <>
          <Spacing size="M" />
          <VoucherSelectorWrap>
            <VoucherSelector
              type="experience"
              evSessions={evSessions}
              onAddToCart={onAddToCart}
              onBookNowClick={onBookNowClick}
              onLinkClick={onLinkClick}
              bookingFieldEntered={bookingFieldEntered}
            />
            <GiftingCallout sessions={evSessions} />
          </VoucherSelectorWrap>
        </>
      ),
    },
  ]

  return (
    <>
      <BookingWrapper id="booking">
        {showGiftingTab && Boolean(evSessions?.length) ? (
          <TabSelector updateActiveTab={activeBookingTab} tabs={bookingTabs} onLinkClick={onLinkClick} />
        ) : (
          renderBookingComponent()
        )}
      </BookingWrapper>
    </>
  )
}

export default ProductBooking
